/**
 * 플랜에 이해관계자·RACI·실제 원가·주간 보고 마감본을 넣는 순수 연산.
 */
import type { CostActual, Project, RaciLetter, Stakeholder, StakeholderLevel, WeeklyReportSnapshot } from "@/engine/types";
import { newId } from "@/store/tree-ops";
import { dropStakeholder, setRaciLetter } from "./raci";

export function addStakeholder(
  project: Project,
  input: { name: string; org: string; role: string; interest: StakeholderLevel; influence: StakeholderLevel },
  id = newId("sh"),
): Project {
  const name = input.name.trim();
  if (!name) return project;
  const person: Stakeholder = {
    id,
    name,
    org: input.org.trim(),
    role: input.role.trim(),
    interest: input.interest,
    influence: input.influence,
  };
  return { ...project, stakeholders: [...(project.stakeholders ?? []), person] };
}

export function updateStakeholder(project: Project, id: string, patch: Partial<Omit<Stakeholder, "id">>): Project {
  const stakeholders = (project.stakeholders ?? []).map((person) => {
    if (person.id !== id) return person;
    const name = patch.name !== undefined ? patch.name.trim() : person.name;
    if (!name) return person;
    return {
      ...person,
      ...patch,
      name,
      org: patch.org !== undefined ? patch.org.trim() : person.org,
      role: patch.role !== undefined ? patch.role.trim() : person.role,
    };
  });
  return { ...project, stakeholders };
}

export function removeStakeholder(project: Project, id: string): Project {
  const next = dropStakeholder(project, id);
  return { ...project, stakeholders: next.stakeholders, raci: next.raci };
}

export function assignRaci(project: Project, taskId: string, stakeholderId: string, letter: RaciLetter | null): Project {
  if (!(project.stakeholders ?? []).some((person) => person.id === stakeholderId)) return project;
  if (!project.tasks.some((task) => task.id === taskId)) return project;
  return { ...project, raci: setRaciLetter(project.raci ?? [], taskId, stakeholderId, letter) };
}

export function setTaskActualCost(project: Project, taskId: string, amount: number | null): Project {
  if (amount != null && (!Number.isFinite(amount) || amount < 0)) return project;
  let changed = false;
  const tasks = project.tasks.map((task) => {
    if (task.id !== taskId) return task;
    changed = true;
    if (amount == null) {
      const { actualCost: _drop, ...rest } = task;
      return rest;
    }
    return { ...task, actualCost: amount };
  });
  return changed ? { ...project, tasks } : project;
}

export function addCostActual(project: Project, input: Omit<CostActual, "id">, id = newId("ca")): Project {
  if (!Number.isFinite(input.amount) || input.amount < 0) return project;
  if (!input.periodStart || !input.periodEnd || input.periodEnd < input.periodStart) return project;
  const row: CostActual = {
    id,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
    amount: input.amount,
    ...(input.taskId ? { taskId: input.taskId } : {}),
    ...(input.note?.trim() ? { note: input.note.trim() } : {}),
  };
  return { ...project, costActuals: [...(project.costActuals ?? []), row] };
}

export function removeCostActual(project: Project, id: string): Project {
  return { ...project, costActuals: (project.costActuals ?? []).filter((row) => row.id !== id) };
}
